/**
 * "Confirm deletion for this profile" (Figma 9397:7188) and the rest of the
 * account-deletion flow:
 *
 * 1. The confirmation lists what happens to Workspaces, Billing and
 *    Data & Privacy.  While the user still owns a multi-user workspace a
 *    warning banner asks them to **transfer ownership** (opens the
 *    Transferring Ownership stepper over those workspaces) or **manually
 *    delete** them (link to Settings > Workspace) first.
 * 2. "Delete Account" asks `GET /users/me/deletion-preview` which owned
 *    workspaces have no other Admin.  Those must be handed over, so the
 *    stepper opens over them ("{i} / {n} workspaces", Next … Done) and each
 *    step posts `transfer-ownership`.  Workspaces that already have another
 *    Admin are skipped — the backend promotes one of them.
 * 3. `DELETE /users/me`, then the full-page success view with "Go to website".
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertBanner, Button, Modal, SparkIcon, SparkModalPortal, sparkToast } from '@spark';

import TransferOwnershipModal from '@/features/workspaces/components/settings/TransferOwnershipModal';
import {
    DeletionSuccessView,
    WORKSPACE_DELETED_GOODBYE,
    goToWebsite,
} from '@/features/workspaces/components/settings/WorkspaceDeletedView';
import accountDeletedIllustration from '@/shared/assets/illustrations/account-deleted.svg';
import { WORKSPACE_TAB } from './settingsTabs.js';
import { deleteAccount, getDeletionPreview } from '../api/accountApi';
import './ProfileInformation.css';

export const DELETE_ACCOUNT_MODAL_WIDTH = 600;
export const DELETE_ACCOUNT_TITLE = 'Confirm deletion for this profile';
export const ACCOUNT_DELETED_TITLE = 'You have successfully deleted your account';
export const DELETE_ACCOUNT_PREVIEW_FAILED = 'Could not check your workspaces. Please try again.';
export const DELETE_ACCOUNT_FAILED = 'Could not delete your account.';
export const WORKSPACE_SETTINGS_PATH = `/settings?tab=${WORKSPACE_TAB.id}`;

export const DELETE_ACCOUNT_COPY = {
    workspaces:
        'Personal workspaces (where you are the only user) will be permanently erased. ' +
        'You will instantly lose access to any shared workspaces you belong to.',
    billing:
        'Any upcoming charges for workspaces slated for deletion will be automatically canceled. ' +
        'Billing for active shared workspaces will continue normally.',
    billingNote:
        'If active workspaces are billed to your personal credit card, please update the payment method ' +
        'to a company card before leaving.',
    privacy: 'Your personal account data will be removed',
};

/** Owned workspaces with other members — the ones the warning banner is about. */
export function sharedOwnedWorkspaces(ownedWorkspaces) {
    return (ownedWorkspaces || []).filter((workspace) => Number(workspace?.member_count) > 1);
}

/** Owned workspaces the backend refuses to hand over by itself (no other Admin). */
export function transfersRequired(ownedWorkspaces) {
    return (ownedWorkspaces || []).filter((workspace) => Boolean(workspace?.requires_transfer));
}

/** "{i} / {n} workspaces" — only when the stepper spans more than one workspace. */
export function stepLabelFor(index, total) {
    return total > 1 ? `${index + 1} / ${total} workspaces` : undefined;
}

export default function DeleteAccountModal({ open, onClose }) {
    const navigate = useNavigate();
    // Owned workspaces from the deletion preview; `null` until loaded.
    const [owned, setOwned] = useState(null);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleted, setDeleted] = useState(false);
    // Stepper state: the workspaces still to hand over, the current index and
    // whether the account is deleted once the last one is done.
    const [transfer, setTransfer] = useState(null);

    const loadPreview = useCallback(async () => {
        setLoadingPreview(true);
        const result = await getDeletionPreview();
        setLoadingPreview(false);
        if (!result.ok) {
            setOwned(null);
            return { ok: false, error: result.error || DELETE_ACCOUNT_PREVIEW_FAILED };
        }
        const list = result.data?.owned_workspaces || [];
        setOwned(list);
        return { ok: true, owned: list };
    }, []);

    useEffect(() => {
        if (!open) return;
        setTransfer(null);
        loadPreview();
    }, [open, loadPreview]);

    const shared = useMemo(() => sharedOwnedWorkspaces(owned), [owned]);
    const busy = deleting || loadingPreview;

    const close = () => {
        if (deleting) return;
        setTransfer(null);
        onClose?.();
    };

    const runDelete = async () => {
        setDeleting(true);
        const result = await deleteAccount();
        setDeleting(false);
        if (!result.ok) {
            sparkToast.error(result.error || DELETE_ACCOUNT_FAILED);
            // A 409 means a workspace still needs an owner; refresh so the
            // next attempt opens the stepper over it.
            if (result.status === 409) loadPreview();
            return;
        }
        setDeleted(true);
    };

    const startTransfers = (workspaces, thenDelete) => {
        setTransfer({ workspaces, index: 0, thenDelete });
    };

    const confirmDelete = async () => {
        if (busy) return;
        let list = owned;
        if (!list) {
            const loaded = await loadPreview();
            if (!loaded.ok) {
                sparkToast.error(loaded.error);
                return;
            }
            list = loaded.owned;
        }
        const required = transfersRequired(list);
        if (required.length > 0) {
            startTransfers(required, true);
            return;
        }
        await runDelete();
    };

    const handleTransferred = ({ workspace } = {}) => {
        const current = transfer.workspaces[transfer.index];
        // No longer owned: drop it from the preview so the banner and the
        // next "Delete Account" reflect the hand-over.
        setOwned((prev) => (prev || []).filter((item) => item.id !== (workspace?.id || current.id)));
        const nextIndex = transfer.index + 1;
        if (nextIndex < transfer.workspaces.length) {
            setTransfer({ ...transfer, index: nextIndex });
            return;
        }
        const { thenDelete } = transfer;
        setTransfer(null);
        if (thenDelete) runDelete();
    };

    const goToWorkspaceSettings = () => {
        close();
        navigate(WORKSPACE_SETTINGS_PATH);
    };

    if (deleted) {
        return (
            <DeletionSuccessView
                illustration={accountDeletedIllustration}
                title={ACCOUNT_DELETED_TITLE}
                message={WORKSPACE_DELETED_GOODBYE}
                buttonLabel="Go to website"
                onAction={goToWebsite}
            />
        );
    }

    const link = (label, onClick) => (
        <Button type="button" variant="link" size="small" className="delete-account-modal__link" onClick={onClick}>
            {label}
        </Button>
    );

    const step = transfer ? transfer.workspaces[transfer.index] : null;

    return (
        <>
            <SparkModalPortal open={open && !transfer} onClose={close} width={DELETE_ACCOUNT_MODAL_WIDTH} destroyOnClose>
                <Modal
                    open
                    danger
                    className="workspace-danger-modal delete-account-modal"
                    title={
                        <span className="workspace-danger-modal__title">
                            <SparkIcon slug="trash" size="medium" />
                            {DELETE_ACCOUNT_TITLE}
                        </span>
                    }
                    showSubtitle={false}
                    showOverlay={false}
                    showScrollBar={false}
                    fixedHeight={false}
                    primaryLabel="Delete Account"
                    primaryIcon={deleting ? <SparkIcon slug="loader" size="small" className="spark-icon-spin" /> : undefined}
                    primaryDisabled={busy}
                    tertiaryLabel="Cancel"
                    tertiaryDisabled={deleting}
                    onPrimaryClick={confirmDelete}
                    onTertiaryClick={close}
                    onCloseClick={close}
                >
                    <div className="delete-account-modal__body" aria-busy={busy || undefined}>
                        <section className="delete-account-modal__section">
                            <h4 className="delete-account-modal__heading">Workspaces</h4>
                            <p className="delete-account-modal__text">{DELETE_ACCOUNT_COPY.workspaces}</p>
                            {shared.length > 0 ? (
                                <AlertBanner
                                    feedback="error"
                                    showButton={false}
                                    showCloseButton={false}
                                    text={
                                        <>
                                            To avoid disrupting your team&apos;s active models and workflows, please{' '}
                                            {link('transfer ownership', () => startTransfers(shared, false))} or{' '}
                                            {link('manually delete', goToWorkspaceSettings)} multi-user workspaces
                                            beforehand. Manage your workspaces {link('here', goToWorkspaceSettings)}.
                                        </>
                                    }
                                />
                            ) : null}
                        </section>
                        <section className="delete-account-modal__section">
                            <h4 className="delete-account-modal__heading">Billing</h4>
                            <p className="delete-account-modal__text">{DELETE_ACCOUNT_COPY.billing}</p>
                            <p className="delete-account-modal__text">
                                <strong>Note:</strong> {DELETE_ACCOUNT_COPY.billingNote}
                            </p>
                        </section>
                        <section className="delete-account-modal__section">
                            <h4 className="delete-account-modal__heading">Data &amp; Privacy</h4>
                            <p className="delete-account-modal__text">{DELETE_ACCOUNT_COPY.privacy}</p>
                        </section>
                    </div>
                </Modal>
            </SparkModalPortal>

            {step ? (
                <TransferOwnershipModal
                    open
                    workspaceId={step.id}
                    workspaceName={step.name}
                    stepLabel={stepLabelFor(transfer.index, transfer.workspaces.length)}
                    primaryLabel={transfer.index < transfer.workspaces.length - 1 ? 'Next' : 'Done'}
                    onClose={() => setTransfer(null)}
                    onTransferred={handleTransferred}
                />
            ) : null}
        </>
    );
}
